// Route POIs (fuel / viewpoints / food) via our Overpass proxy + cache.
import { haversineKm } from '../utils/geomath';

const GET_POIS_URL = 'https://us-central1-roamr-4f8ad.cloudfunctions.net/getRoutePOIs';

// Sample the route ~every `stepKm` (capped) so the Overpass `around` polyline
// stays light. Radius in the function (2 km) covers the gaps between samples.
export function sampleRouteForPOI(coords, stepKm = 3, maxPts = 120) {
  if (!coords || coords.length < 2) return [];
  const cum = [0];
  for (let i = 1; i < coords.length; i++) {
    cum[i] = cum[i - 1] + haversineKm(coords[i - 1][1], coords[i - 1][0], coords[i][1], coords[i][0]);
  }
  const total = cum[cum.length - 1];
  if (total <= 0) return [coords[0]];

  const step = Math.max(stepKm, total / maxPts);
  const pts = [];
  let target = 0, i = 0;
  while (target <= total && pts.length < maxPts) {
    while (i < cum.length - 1 && cum[i] < target) i++;
    pts.push(coords[i]);
    target += step;
  }
  const last = coords[coords.length - 1];
  if (pts[pts.length - 1] !== last) pts.push(last);
  return pts;
}

export async function fetchRoutePOIs(geometry, signal) {
  const coords = geometry?.coordinates;
  if (!coords || coords.length < 2) return null;
  const pts = sampleRouteForPOI(coords).map(([lng, lat]) => [lng, lat]);

  const res = await fetch(GET_POIS_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ points: pts }),
    signal,
  });
  if (!res.ok) return null;
  const data = await res.json().catch(() => null);
  if (!data || !Array.isArray(data.fuel)) return null;
  return { fuel: data.fuel, viewpoint: data.viewpoint || [], food: data.food || [] };
}

const GET_DISCOVERIES_URL = 'https://us-central1-roamr-4f8ad.cloudfunctions.net/getRouteDiscoveries';

// "Worth a stop" — notable places along the route from Wikipedia, with
// image + one-line description. Returns [{ id, title, extract, thumb, lat, lng, url }].
export async function fetchRouteDiscoveries(geometry, signal) {
  const coords = geometry?.coordinates;
  if (!coords || coords.length < 2) return null;
  const pts = sampleRouteForPOI(coords).map(([lng, lat]) => [lng, lat]);

  const res = await fetch(GET_DISCOVERIES_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ points: pts }),
    signal,
  });
  if (!res.ok) return null;
  const data = await res.json().catch(() => null);
  return Array.isArray(data?.places) ? data.places : null;
}

const GET_DISCOVERY_IMAGES_URL = 'https://us-central1-roamr-4f8ad.cloudfunctions.net/getDiscoveryImages';

// Extra photos for one discovery's Wikipedia page, fetched lazily (only when
// the marker is actually hovered) — the initial discoveries call stays cheap
// with just the one page-image thumbnail. Returns an array of image URLs.
export async function fetchDiscoveryImages(title, signal) {
  if (!title) return [];
  const res = await fetch(GET_DISCOVERY_IMAGES_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ title }),
    signal,
  });
  if (!res.ok) return [];
  const data = await res.json().catch(() => null);
  return Array.isArray(data?.images) ? data.images : [];
}
